import { ref } from 'vue';

interface AppDetectionOptions {
  scheme: string;      // 应用的 URL Scheme，如 'jihuanshe://'
  universalLink?: string;  // Universal Link / App Link
  timeout?: number;    // 检测超时时间
}

export function useAppDetection(options: AppDetectionOptions) {
  const isInstalled = ref<boolean | null>(null);
  const isChecking = ref(false);

  // 检查本地存储中的安装状态
  const checkLocalStorage = (): boolean => {
    const stored = localStorage.getItem('app_installed');
    return stored === 'true';
  };

  // 检查客户端桥接方法
  const checkClientBridge = (): boolean => {
    return !!window.clientBridge?.isAppInstalled;
  };

  // 使用 URL Scheme 检测
  const checkUrlScheme = async (): Promise<boolean> => {
    return new Promise((resolve) => {
      const timeout = options.timeout || 2000;
      const startTime = Date.now();
      const schemeUrl = `${options.scheme}://check`;

      // 在隐藏的 iframe 中打开 scheme
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      // 检测页面是否被隐藏（表示应用被打开）
      const checkVisibility = () => {
        const isHidden = document.hidden || (document as any).webkitHidden;
        const timeElapsed = Date.now() - startTime;

        if (isHidden || timeElapsed >= timeout) {
          cleanup();
          resolve(isHidden);
        } else {
          requestAnimationFrame(checkVisibility);
        }
      };

      // 清理函数
      const cleanup = () => {
        document.body.removeChild(iframe);
      };

      // 开始检测
      iframe.src = schemeUrl;
      requestAnimationFrame(checkVisibility);
    });
  };

  // 使用 Universal Links 检测
  const checkUniversalLink = async (): Promise<boolean> => {
    if (!options.universalLink) return false;

    try {
      const response = await fetch(options.universalLink, {
        method: 'HEAD',
        mode: 'no-cors'
      });
      return response.type === 'opaque';
    } catch {
      return false;
    }
  };

  // 综合检测方法
  const checkAppInstalled = async () => {
    if (isChecking.value) return;
    isChecking.value = true;

    try {
      // 1. 首先检查本地存储
      if (checkLocalStorage()) {
        isInstalled.value = true;
        return;
      }

      // 2. 检查客户端桥接
      if (checkClientBridge()) {
        isInstalled.value = true;
        localStorage.setItem('app_installed', 'true');
        return;
      }

      // 3. 检查 Universal Links
      const universalLinkResult = await checkUniversalLink();
      if (universalLinkResult) {
        isInstalled.value = true;
        localStorage.setItem('app_installed', 'true');
        return;
      }

      // 4. 最后尝试 URL Scheme
      const urlSchemeResult = await checkUrlScheme();
      isInstalled.value = urlSchemeResult;
      if (urlSchemeResult) {
        localStorage.setItem('app_installed', 'true');
      }
    } catch (error) {
      console.error('检测应用安装状态失败:', error);
      isInstalled.value = false;
    } finally {
      isChecking.value = false;
    }
  };

  // 重置检测状态
  const resetDetection = () => {
    localStorage.removeItem('app_installed');
    isInstalled.value = null;
  };

  return {
    isInstalled,
    isChecking,
    checkAppInstalled,
    resetDetection
  };
} 